import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { useResumesStore } from 'stores/resumes.js'

const POLL_EVERY = 2000 // how often a running resume is asked about
const GIVE_UP_AFTER = 20 * 60 * 1000 // the server ends a run that long gone after 15 minutes; this stops asking a little later

const messageOf = (res, fallback) => res.response?.message || res.error?.data?.message || fallback
const isActive = (generation) => generation?.status === 'pending' || generation?.status === 'running'

// Writing a resume for a job with AI. The server does the work in the background (about 20 seconds to a couple of minutes), so starting
// only starts it, and this store then asks how it is going until it is done. What it knows is kept per job, so leaving a job and coming back
// shows the same state, and a run that finishes while another job is open still ends up in the CV Builder's list.
export const useResumeGenerationStore = defineStore('resumeGeneration', {
  state: () => ({
    byJob: {}, // job id => the latest run for that job (what the server sent)
    errors: {}, // job id => why a run could not start
    starting: {}, // job id => true while the request that starts a run is in flight
  }),

  getters: {
    // The run to show for a job: the one this store knows, otherwise the latest the job's own payload carried.
    latest: (state) => (job) => state.byJob[job.id] || job.ai_resume?.latest || null,
  },

  actions: {
    // A job opened in full carries its latest run. A run that is still going is picked up and followed.
    adopt(job) {
      const latest = job.ai_resume?.latest
      if (!latest || this.byJob[job.id]) return

      this.byJob[job.id] = latest
      if (isActive(latest)) this.follow(job.id, latest.id)
    },

    // `baseResumeId` is the template to write from, when the user chose one. Without it the default resume is used.
    async start(jobId, baseResumeId = null) {
      this.errors[jobId] = null
      this.starting[jobId] = true
      const { axiosRequest } = useRequest()
      let res
      try {
        res = await axiosRequest('POST', `/api/v1/fetched_jobs/${jobId}/resume_generations`, baseResumeId ? { base_resume_id: baseResumeId } : {}, false)
      } catch {
        res = { js_error: true }
      }
      this.starting[jobId] = false

      if (!res.js_error && res.response?.success) {
        this.byJob[jobId] = res.response.data
        if (isActive(res.response.data)) this.follow(jobId, res.response.data.id)
        return { ok: true }
      }

      this.errors[jobId] = messageOf(res, 'Could not start. Please try again.')
      return { ok: false, message: this.errors[jobId] }
    },

    // Asks about a run every couple of seconds until it has finished.
    follow(jobId, generationId) {
      const startedAt = Date.now()
      const tick = async () => {
        const { axiosRequest } = useRequest()
        let res
        try {
          res = await axiosRequest('GET', `/api/v1/resume_generations/${generationId}`, {}, false)
        } catch {
          res = { js_error: true }
        }

        if (!res.js_error && res.response?.success) {
          this.byJob[jobId] = res.response.data
          if (!isActive(res.response.data)) {
            useResumesStore().invalidate() // the CV Builder's list has a new resume (or the same ones, if it failed)
            return
          }
        }
        // A request that failed is asked again: the run goes on on the server whether or not this page can see it.
        if (Date.now() - startedAt < GIVE_UP_AFTER) setTimeout(tick, POLL_EVERY)
      }
      setTimeout(tick, POLL_EVERY)
    },
  },
})
